import { AquariusClient, Asset as AquariusAsset, type Pool } from '@aquariusdefi/sdk';

import { CONTRACTS } from 'constants/soroban';
import { BASE_FEE } from 'constants/stellar';

import { toBaseUnits, fromBaseUnits } from 'helpers/amount';
import { getEnv, getIsTestnetEnv, getNetworkPassphrase } from 'helpers/env';
import { getAmmAquaUrl, getHorizonUrl, getSorobanUrl } from 'helpers/url';

import { getAmountByAsset } from 'services/soroban/utils/scValHelpers';

import { Token, TokenType } from 'types/token';

const clients = new Map<string, AquariusClient>();
const pools = new WeakMap<AquariusClient, Map<string, Promise<Pool>>>();

export const getAquariusClient = (accountId?: string): AquariusClient => {
    const env = getEnv();
    const key = `${env}:${accountId ?? ''}`;
    if (!clients.has(key)) {
        clients.set(
            key,
            new AquariusClient({
                network: getIsTestnetEnv() ? 'testnet' : 'mainnet',
                rpcUrl: getSorobanUrl(),
                horizonUrl: getHorizonUrl(),
                apiUrl: `${getAmmAquaUrl()}/api/external/v2`,
                contracts: { router: CONTRACTS[env].amm, batch: CONTRACTS[env].batch },
                baseFee: BASE_FEE,
                timeoutSeconds: 'infinite',
                signer: accountId
                    ? {
                          publicKey: () => accountId,
                          sign: () => {
                              throw new Error('Use the connected wallet to sign SDK transactions.');
                          },
                      }
                    : undefined,
            }),
        );
    }
    return clients.get(key);
};

export const getAquariusPool = (address: string, accountId?: string): Promise<Pool> => {
    const client = getAquariusClient(accountId);
    if (!pools.has(client)) pools.set(client, new Map());
    const cache = pools.get(client);
    if (!cache.has(address)) {
        const request = client.pools.byAddress(address).catch(error => {
            cache.delete(address);
            throw error;
        });
        cache.set(address, request);
    }
    return cache.get(address);
};

export const toAquariusAsset = (token: Token): AquariusAsset => {
    if (token.type === TokenType.soroban) return AquariusAsset.contract(token.contract);
    const asset = token.isNative()
        ? AquariusAsset.native()
        : AquariusAsset.classic(token.code, token.issuer);
    if (asset.contractId(getNetworkPassphrase()) !== token.contract) {
        throw new Error('Token contract does not match the selected asset.');
    }
    return asset;
};

export const toDepositAmounts = (
    tokens: Token[],
    amounts: Map<string, string>,
): [string, bigint][] =>
    tokens.map(token => [
        token.contract,
        toBaseUnits(getAmountByAsset(amounts, token), token.decimal),
    ]);

export const amountsForTokens = (
    amounts: bigint[],
    tokenIds: string[],
    tokens: Token[],
): string[] =>
    tokens.map(token => fromBaseUnits(amounts[tokenIds.indexOf(token.contract)], token.decimal));
